import { useState, useMemo, useCallback } from 'react';
import {
	createSongNote,
	setSongNoteStatus,
	updateSongNoteText,
	updateSongNoteSection,
	deleteSongNote,
} from '../../../../services/SongNotesService';
import { ensureRepertoireEntry } from '../../../../services/RepertoireService';
import {
	SONG_NOTE_STATUS,
	isDelivered,
	isLegalTransition,
	partitionNotesWrittenOrder,
} from '../../../../services/songNotesModel';

const { CAPTURED } = SONG_NOTE_STATUS;

/**
 * Note operations over the song board — extracted from SongNotes.js (N1b) as a
 * logic move. The hard-won behaviors carry over verbatim:
 *
 *  - FAST JOT: add is text-only, no status picker; the note is BORN `captured`
 *    (teacher-only until deliberately delivered through the Deliver dialog).
 *  - OPTIMISTIC EVERYTHING: insert/status/delete apply locally first so the tap
 *    feels instant mid-lesson, then reconcile with Firestore; on failure they
 *    roll back and surface an error instead of leaving a note the teacher
 *    believes is saved.
 *  - LOCAL-ONLY MODE: with no studentId there is no (student × song) key, so
 *    notes hold in local state (`local-` ids). They are NOT lost — flushLocal()
 *    persists them once a student is linked (jot bar is never disabled).
 *
 * The UI gates delivery: setNoteStatus refuses illegal transitions, but the
 * "are you sure — this shows it to the family and can't go back" moment lives
 * in DeliverDialog, which calls this after confirmation.
 */
const useSongNotes = ({
	board, // the useSongBoard return value
	lessonId = null,
	createdBy = null,
	onNotesPersisted = null,
}) => {
	const { songs, setSongs, currentSong, currentSongId, setCurrentSongId, isPersistent, studentId } = board;
	const [saveError, setSaveError] = useState('');

	/** Replace one note in local state without disturbing the rest of the tree. */
	const patchNoteInState = useCallback(
		(songId, noteId, patch) => {
			setSongs((prev) =>
				prev.map((song) =>
					song.id !== songId
						? song
						: { ...song, notes: song.notes.map((n) => (n.id === noteId ? { ...n, ...patch } : n)) }
				)
			);
		},
		[setSongs]
	);

	/**
	 * Add a note to a song (defaults to the current song). Returns true when the
	 * text was accepted — the jot bar clears immediately and re-focuses (the
	 * self-replenishing field is the caller's concern).
	 */
	const addNote = useCallback(
		async (rawText, songIdArg = null, section = null) => {
			const text = (rawText || '').trim();
			// The section chip that was active when the note was jotted (null = whole song).
			const sectionFields = section ? { sectionId: section.id, sectionLabel: section.label } : { sectionId: null, sectionLabel: null };
			const song = songIdArg ? songs.find((s) => s.id === songIdArg) : currentSong;
			if (!text || !song) return false;

			setSaveError('');

			if (!isPersistent) {
				// Unlinked lesson — hold locally; flushLocal persists after linking.
				setSongs((prev) =>
					prev.map((s) =>
						s.id !== song.id
							? s
							: {
									...s,
									notes: [
										...s.notes,
										{ id: `local-${Date.now()}`, text, status: CAPTURED, statusChangedAt: new Date(), local: true, ...sectionFields },
									],
								}
					)
				);
				return true;
			}

			const tempId = `pending-${Date.now()}`;
			setSongs((prev) =>
				prev.map((s) =>
					s.id !== song.id
						? s
						: {
								...s,
								notes: [
									...s.notes,
									{ id: tempId, text, status: CAPTURED, statusChangedAt: new Date(), pending: true, ...sectionFields },
								],
							}
				)
			);

			try {
				const created = await createSongNote({
					studentId,
					songId: song.id,
					songName: song.name,
					text,
					originLessonId: lessonId,
					createdBy,
					...sectionFields,
				});
				patchNoteInState(song.id, tempId, { ...created, pending: false });
				if (onNotesPersisted) onNotesPersisted();
				return true;
			} catch (error) {
				console.error('Error creating Song Note:', error);
				// Roll the optimistic row back out rather than leaving a note the
				// teacher believes is saved.
				setSongs((prev) =>
					prev.map((s) =>
						s.id !== song.id ? s : { ...s, notes: s.notes.filter((n) => n.id !== tempId) }
					)
				);
				setSaveError('That note did not save. It is still in the box — try again.');
				return false;
			}
		},
		[songs, currentSong, isPersistent, setSongs, studentId, lessonId, createdBy, onNotesPersisted, patchNoteInState]
	);

	/**
	 * Move a note between states. Explicit choice, never a cycle; illegal
	 * transitions (anything back to `captured`) are refused by the model.
	 * The captured→delivered crossing MUST arrive here via DeliverDialog.
	 */
	const setNoteStatus = useCallback(
		async (noteId, nextStatus) => {
			const song = songs.find((s) => s.notes.some((n) => n.id === noteId));
			const note = song?.notes.find((n) => n.id === noteId);
			if (!note || note.pending) return false;

			const current = note.status;
			if (current === nextStatus || !isLegalTransition(current, nextStatus)) return false;

			setSaveError('');
			// Optimistic: the tap must feel instant, especially the one-tap landing.
			patchNoteInState(song.id, noteId, { status: nextStatus, statusChangedAt: new Date() });

			if (!isPersistent || note.local) return true;

			try {
				await setSongNoteStatus(noteId, {
					from: current,
					to: nextStatus,
					lessonId,
					byTeacher: createdBy,
				});
				if (onNotesPersisted) onNotesPersisted();
				return true;
			} catch (error) {
				console.error('Error updating Song Note status:', error);
				patchNoteInState(song.id, noteId, { status: current });
				setSaveError('Status did not save. Try again.');
				return false;
			}
		},
		[songs, isPersistent, patchNoteInState, lessonId, createdBy, onNotesPersisted]
	);

	/**
	 * Edit a note's text. Allowed at any state — but visibility never reverts;
	 * a delivered note stays delivered (the dialog says so at delivery time).
	 */
	const updateNoteText = useCallback(
		async (noteId, rawText) => {
			const text = (rawText || '').trim();
			const song = songs.find((s) => s.notes.some((n) => n.id === noteId));
			const note = song?.notes.find((n) => n.id === noteId);
			if (!text || !note || note.pending) return false;

			const previous = note.text;
			patchNoteInState(song.id, noteId, { text });

			if (!isPersistent || note.local) return true;

			try {
				await updateSongNoteText(noteId, text);
				if (onNotesPersisted) onNotesPersisted();
				return true;
			} catch (error) {
				console.error('Error editing Song Note:', error);
				patchNoteInState(song.id, noteId, { text: previous });
				setSaveError('That edit did not save. Try again.');
				return false;
			}
		},
		[songs, isPersistent, patchNoteInState, onNotesPersisted]
	);

	/**
	 * Move a note to another song section (or `null` = whole song). Optimistic,
	 * like every other note write; visibility never changes.
	 */
	const setNoteSection = useCallback(
		async (noteId, section) => {
			const song = songs.find((s) => s.notes.some((n) => n.id === noteId));
			const note = song?.notes.find((n) => n.id === noteId);
			if (!note || note.pending) return false;
			const next = section ? { sectionId: section.id, sectionLabel: section.label } : { sectionId: null, sectionLabel: null };
			if ((note.sectionId || null) === next.sectionId) return true;
			const previous = { sectionId: note.sectionId || null, sectionLabel: note.sectionLabel || null };
			patchNoteInState(song.id, noteId, next);
			if (!isPersistent || note.local) return true;
			try {
				await updateSongNoteSection(noteId, next);
				return true;
			} catch (error) {
				console.error('Error moving Song Note section:', error);
				patchNoteInState(song.id, noteId, previous);
				setSaveError('That section change did not save. Try again.');
				return false;
			}
		},
		[songs, isPersistent, patchNoteInState]
	);

	/**
	 * Delete a note. Deleting a `captured` note is free — nobody ever saw it.
	 * The delivered-note confirm ("the family may have read this") is UI's job;
	 * `needsDeleteConfirm` tells it which case it is in.
	 */
	const deleteNote = useCallback(
		async (noteId) => {
			const song = songs.find((s) => s.notes.some((n) => n.id === noteId));
			const note = song?.notes.find((n) => n.id === noteId);
			if (!note) return false;

			const snapshot = song.notes;
			setSongs((prev) =>
				prev.map((s) => (s.id !== song.id ? s : { ...s, notes: s.notes.filter((n) => n.id !== noteId) }))
			);

			if (!isPersistent || note.pending || note.local) return true;

			try {
				await deleteSongNote(noteId);
				if (onNotesPersisted) onNotesPersisted();
				return true;
			} catch (error) {
				console.error('Error deleting Song Note:', error);
				setSongs((prev) => prev.map((s) => (s.id !== song.id ? s : { ...s, notes: snapshot })));
				setSaveError('Could not delete that note.');
				return false;
			}
		},
		[songs, setSongs, isPersistent, onNotesPersisted]
	);

	const needsDeleteConfirm = useCallback(
		(noteId) => {
			const song = songs.find((s) => s.notes.some((n) => n.id === noteId));
			const note = song?.notes.find((n) => n.id === noteId);
			return Boolean(note && isDelivered(note.status));
		},
		[songs]
	);

	/**
	 * Persist every `local` note after a student is linked mid-lesson. Local ids
	 * are swapped for real document rows; failures stay local (and keep the
	 * `local` flag) so nothing is dropped.
	 *
	 * LOCAL SONGS RESOLVE FIRST — this is the orphan mint, found live 2026-08-05.
	 * A song added to an unlinked board carries `local-${Date.now()}` as its id
	 * (correct at that moment: no student, no repertoire key). This flush used to
	 * swap ids for the NOTES and never for the SONGS, so every flushed note was
	 * persisted with `songId: "local-…"` — a foreign key pointing at nothing,
	 * visible in the carry-forward rail and permanently undeliverable ("Crazy for
	 * you", three notes, one dead id). So: every local-id song gets a real
	 * repertoire row via ensureRepertoireEntry before any note persists, and a
	 * song whose resolution FAILS keeps its notes local rather than minting
	 * orphans — nothing is dropped, and nothing is written that points at nothing.
	 */
	const flushLocal = useCallback(
		async ({ studentId: linkedStudentId }) => {
			if (!linkedStudentId) return;

			const realIdFor = new Map(); // board-local song id → repertoire doc id
			for (const song of songs) {
				if (!String(song.id).startsWith('local-')) continue;
				try {
					const { id } = await ensureRepertoireEntry(linkedStudentId, song.name, song.metadata);
					realIdFor.set(song.id, id);
				} catch (error) {
					console.error('Error resolving repertoire row for local song:', error);
					// Leave unmapped — its notes stay local below.
				}
			}
			if (realIdFor.size) {
				setSongs((prev) =>
					prev.map((s) => (realIdFor.has(s.id) ? { ...s, id: realIdFor.get(s.id) } : s))
				);
				// currentSong is derived by id; a swapped id would otherwise bounce
				// the teacher back to Warm-ups mid-link.
				if (realIdFor.has(currentSongId)) setCurrentSongId(realIdFor.get(currentSongId));
			}

			const locals = [];
			for (const song of songs) {
				for (const note of song.notes) {
					if (note.local) locals.push({ song, note });
				}
			}
			let flushed = 0;
			for (const { song, note } of locals) {
				const songId = realIdFor.get(song.id) || song.id;
				if (String(songId).startsWith('local-')) {
					// Resolution failed above. A persisted note with this id would be
					// born orphaned — keeping it local is the honest failure.
					setSaveError('Some notes could not be saved to the student. They are still on the board.');
					continue;
				}
				try {
					const created = await createSongNote({
						studentId: linkedStudentId,
						songId,
						songName: song.name,
						text: note.text,
						originLessonId: lessonId,
						createdBy,
						sectionId: note.sectionId || null,
						sectionLabel: note.sectionLabel || null,
					});
					// State now carries the swapped song id — patch against it.
					patchNoteInState(songId, note.id, { ...created, local: false });
					flushed += 1;
				} catch (error) {
					console.error('Error flushing local Song Note:', error);
					setSaveError('Some notes could not be saved to the student. They are still on the board.');
				}
			}
			if (flushed && onNotesPersisted) onNotesPersisted();
		},
		[songs, setSongs, currentSongId, setCurrentSongId, lessonId, createdBy, patchNoteInState, onNotesPersisted]
	);

	// Landed notes leave the primary list. Open notes read in the order they
	// were WRITTEN (oldest first, like the song); landed stay newest-change-first
	// so a just-landed note tops the drawer as immediate feedback.
	const { open: openNotes, landed: landedNotes } = useMemo(
		() => partitionNotesWrittenOrder(currentSong?.notes || []),
		[currentSong]
	);

	const noteCounts = useMemo(() => {
		const notes = currentSong?.notes || [];
		return {
			captured: notes.filter((n) => n.status === SONG_NOTE_STATUS.CAPTURED).length,
			focus: notes.filter((n) => n.status === SONG_NOTE_STATUS.FOCUS).length,
			landed: notes.filter((n) => n.status === SONG_NOTE_STATUS.LANDED).length,
		};
	}, [currentSong]);

	// Wrap-up tallies run across the WHOLE board, not just the current song.
	const boardCounts = useMemo(() => {
		const all = songs.flatMap((s) => s.notes);
		return {
			captured: all.filter((n) => n.status === SONG_NOTE_STATUS.CAPTURED).length,
			focus: all.filter((n) => n.status === SONG_NOTE_STATUS.FOCUS).length,
			landed: all.filter((n) => n.status === SONG_NOTE_STATUS.LANDED).length,
		};
	}, [songs]);

	return {
		addNote,
		setNoteStatus,
		updateNoteText,
		setNoteSection,
		deleteNote,
		needsDeleteConfirm,
		flushLocal,
		openNotes,
		landedNotes,
		noteCounts,
		boardCounts,
		saveError,
		clearSaveError: () => setSaveError(''),
	};
};

export default useSongNotes;
